import React, { useState, useRef, useEffect } from 'react';
import { featuresData as defaultFeaturesData, FeatureItem } from '../../data/featuresData';
import { generalMedia } from '../../data/generalMedia';
import { useCmsSection } from '../../cms/useCmsSection';
import { applyFeaturesPatch } from '../../cms/apply';

export function FeaturesCarousel() {
  const [active, setActive] = useState(0);

  // CMS data
  const { data: cmsFeatures, loading, error } = useCmsSection<FeatureItem[]>(
    'features',
    [...defaultFeaturesData],
    applyFeaturesPatch
  );
  const features = (cmsFeatures?.length ? cmsFeatures : defaultFeaturesData) ?? [];
  const total = features.length;
  const slide = features[active] ?? features[0];

  // BG (optional)
  const bg = generalMedia['greatchild-playground-photo']?.src ?? '';

  // Swipe
  const startX = useRef<number | null>(null);
  const endX = useRef<number | null>(null);
  const minSwipe = 50;

  const onTouchStart = (e: React.TouchEvent) => {
    endX.current = null;
    startX.current = e.targetTouches[0].clientX;
  };
  const onTouchMove = (e: React.TouchEvent) => {
    endX.current = e.targetTouches[0].clientX;
  };
  const onTouchEnd = () => {
    if (startX.current == null || endX.current == null) return;
    const dist = startX.current - endX.current;
    if (Math.abs(dist) < minSwipe) return;
    setActive((prev) => (dist > 0 ? Math.min(prev + 1, total - 1) : Math.max(prev - 1, 0)));
  };

  // Keyboard
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'ArrowRight') setActive((p) => Math.min(p + 1, total - 1));
      if (e.key === 'ArrowLeft') setActive((p) => Math.max(p - 1, 0));
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [total]);

  if (error || !total || loading) {
    return <div className="py-4" />;
  }

  return (
    <section className="px-4 py-8 md:py-10">
      <div
        className="mx-auto w-full max-w-screen-xl"
        role="group"
        aria-label={`${active + 1} of ${total}`}
      >
        <div
          className="
            relative grid md:grid-cols-2 items-stretch gap-6
            rounded-2xl bg-slate-100 shadow-sm
            md:min-h-[460px] lg:min-h-[520px] xl:min-h-[560px]
            touch-pan-y
          "
          onTouchStart={onTouchStart}
          onTouchMove={onTouchMove}
          onTouchEnd={onTouchEnd}
        >
          {/* Text column */}
          <div className="h-full flex md:items-center">
            <div
              className="
                p-6 md:p-10 w-full max-w-[640px] mx-auto
                flex flex-col gap-5
                items-start md:items-center
                text-left md:text-center
              "
            >
              <h2 className="text-slate-700 text-2xl md:text-4xl font-semibold leading-tight">
                {slide.title}
              </h2>

              <p className="text-slate-600 text-sm md:text-base md:max-w-[56ch]">
                {slide.description}
              </p>

              {slide.buttonText?.length > 0 && slide.buttonLink?.length > 1 && (
                <a
                  href={slide.buttonLink}
                  className="
                    inline-flex items-center justify-center
                    px-6 py-2.5 text-sm
                    md:px-8 md:py-3 md:text-base
                    lg:px-10 lg:py-3.5 lg:text-lg
                    rounded-full bg-purple-800 text-white font-medium
                  "
                >
                  {slide.buttonText}
                </a>
              )}
            </div>
          </div>

          {/* Image column */}
          <div className="relative w-full">
            {bg && (
              <div
                className="absolute inset-0 bg-center bg-contain md:bg-cover opacity-20"
                style={{ backgroundImage: `url(${bg})`}}
                aria-hidden="true"
              />
            )}
            <img
              src={slide.image}
              alt={slide.imageAlt}
              width={1536}
              height={2048}
              loading="lazy"
              className="
                relative w-full h-auto object-contain p-4
                max-h-[65vh] md:max-h-none md:h-full md:w-full
              "
            />
          </div>
        </div>

        {/* Dots */}
        <div className="flex justify-center gap-2 mt-5">
          {features.map((_, i) => (
            <button
              key={i}
              onClick={() => setActive(i)}
              aria-label={`Go to slide ${i + 1}`}
              aria-pressed={i === active}
              className={`h-3.5 w-3.5 rounded-full transition ${
                i === active ? 'bg-cyan-500' : 'bg-neutral-300 hover:bg-neutral-400'
              }`}
            />
          ))}
        </div>
      </div>
    </section>
  );
}
